import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useT } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Copy, Gift, Check, Twitter, Send } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

export const ReferralCard = ({ compact = false }) => {
  const { t, lang } = useT();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get('/referrals/me').then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;

  const link = `${window.location.origin}/register?ref=${data.referral_code}`;
  const labels = lang === 'fr' ? {
    title: 'Parraine un ami, gagne 1 mois',
    desc: 'Partage ton code. Quand un ami s\'inscrit, vous gagnez TOUS LES DEUX 1 mois de Créateur offert.',
    yourCode: 'Ton code',
    copyLink: 'Copier le lien',
    copied: 'Copié !',
    shareTw: 'X / Twitter',
    shareWa: 'WhatsApp',
    statsFriends: 'amis parrainés',
    statsMonths: 'mois gagnés',
    msg: `Je viens de tester HighlightAI - une IA qui transforme tes longues vidéos en clips viraux. Utilise mon code ${data.referral_code} pour 1 mois gratuit Créateur 👉 ${link}`,
  } : {
    title: 'Refer a friend, earn 1 month',
    desc: 'Share your code. When a friend signs up, BOTH of you get 1 month of Creator free.',
    yourCode: 'Your code',
    copyLink: 'Copy link',
    copied: 'Copied!',
    shareTw: 'X / Twitter',
    shareWa: 'WhatsApp',
    statsFriends: 'friends referred',
    statsMonths: 'months earned',
    msg: `Just tried HighlightAI - AI that turns your long videos into viral clips. Use my code ${data.referral_code} for 1 month free Creator 👉 ${link}`,
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success(labels.copied);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copy failed');
    }
  };

  const tweet = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(labels.msg)}`;
    window.open(url, '_blank', 'noopener');
  };

  const whatsapp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(labels.msg)}`;
    window.open(url, '_blank', 'noopener');
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`relative overflow-hidden rounded-2xl border border-[#D4FF00]/30 bg-gradient-to-br from-[#D4FF00]/8 via-black/50 to-black p-6 ${compact ? '' : 'md:p-8'}`} data-testid="referral-card">
      <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-[#D4FF00]/10 blur-[60px]" />

      <div className="relative">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[#D4FF00] flex items-center justify-center shrink-0">
            <Gift className="w-5 h-5 text-black" strokeWidth={2.5} />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-xl font-black tracking-tight" data-testid="referral-title">{labels.title}</h3>
            <p className="text-zinc-400 text-sm mt-0.5">{labels.desc}</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 my-5">
          <div className="text-center p-3 rounded-xl bg-black/40 border border-white/5">
            <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{labels.yourCode}</div>
            <div className="font-heading text-lg font-black text-[#D4FF00] tracking-wider" data-testid="referral-code">{data.referral_code}</div>
          </div>
          <div className="text-center p-3 rounded-xl bg-black/40 border border-white/5">
            <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{labels.statsFriends}</div>
            <div className="font-heading text-2xl font-black" data-testid="referral-friends">{data.referrals_count}</div>
          </div>
          <div className="text-center p-3 rounded-xl bg-black/40 border border-white/5">
            <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{labels.statsMonths}</div>
            <div className="font-heading text-2xl font-black text-[#D4FF00]" data-testid="referral-months">{data.referrals_months_earned}</div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg bg-black/60 border border-white/10 text-xs text-zinc-300 font-mono truncate">
            {link}
          </div>
          <Button onClick={copy} className="bg-[#D4FF00] text-black hover:bg-[#B3D600] font-semibold" data-testid="referral-copy-btn">
            {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />} {copied ? labels.copied : labels.copyLink}
          </Button>
        </div>

        <div className="flex gap-2 mt-3">
          <Button onClick={tweet} variant="outline" size="sm" className="border-white/10 bg-transparent text-xs flex-1" data-testid="referral-tweet-btn">
            <Twitter className="w-3 h-3 mr-1" /> {labels.shareTw}
          </Button>
          <Button onClick={whatsapp} variant="outline" size="sm" className="border-white/10 bg-transparent text-xs flex-1" data-testid="referral-wa-btn">
            <Send className="w-3 h-3 mr-1" /> {labels.shareWa}
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default ReferralCard;
